import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Autocomplete, TextField, Tooltip } from '@mui/material';

const AddressAutocomplete = forwardRef(
  (
    {
      fontSize,
      inputRef,
      disabled,
      locationFlow,
      onInputChange,
      onSelectDetails,
      defaultValue = '',
      inputVariant,
      label = '',
      validattionErrors,
      errorMsg,
      inputType
    },
    ref
  ) => {
    const [options, setOptions] = useState([]);
    const [inputValueApi, setInputValueApi] = useState(defaultValue);
    const [inputValue, setInputValue] = useState(defaultValue);
    const [highlightedOption, setHighlightedOption] = useState(null);
    const [addressError, setAddressError] = useState('');
    const [userOriginalInput, setUserOriginalInput] = useState('');
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [lastSelectedType, setLastSelectedType] = useState(null); // 'manual' or 'google'
    const autocompleteService = useRef(null);

    // --- INITIALIZE DEFAULT VALUE ---
    useEffect(() => {
      setInputValue(defaultValue);
    }, [defaultValue]);

    // --- SETUP GOOGLE AUTOCOMPLETE SERVICE ---
    useEffect(() => {
      if (!autocompleteService.current && window.google) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
      }
    }, []);

    // --- PARSE USER TYPED ADDRESS ---
    const parseUserTypedAddress = address => {
      const result = {
        company: '',
        streetLines: address,
        city: '',
        state: '',
        zip: '',
        country: 'USA',
        isUserTyped: true
      };

      try {
        // Try to extract city, state, zip from patterns like:
        // "CITY, STATE ZIP" or "CITY, STATE ZIP, COUNTRY"
        const cityStateZipRegex =
          /([A-Za-z\s]+),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)\s*,?\s*([A-Za-z]*)$/;
        const match = address.match(cityStateZipRegex);

        if (match) {
          result.city = match[1].trim();
          result.state = match[2].trim();
          result.zip = match[3].trim();
          if (match[4]) result.country = match[4].trim();

          // Remove city/state/zip to get street address
          result.streetLines = address.replace(cityStateZipRegex, '').trim();
        }

        // For quickQuote, try to extract company name from beginning
        if (inputType === 'quickQuote' && result.streetLines) {
          const parts = result.streetLines.split(/\s+(?=\d)/); // Split before numbers
          if (parts.length > 1 && /^[A-Za-z\s&.,'-]+$/.test(parts[0].trim())) {
            result.company = parts[0].trim();
            result.streetLines = parts.slice(1).join(' ').trim();
          }
        }
      } catch (error) {
        console.log('Error parsing user address:', error);
        // Fallback: keep original address as streetLines
        result.streetLines = address;
      }

      return result;
    };

    // --- CREATE CUSTOM SUGGESTION FOR DROPDOWN ---
    const createCustomSuggestion = address => {
      const parsed = parseUserTypedAddress(address);

      return {
        place_id: 'custom_user_typed',
        description: address,
        structured_formatting: {
          main_text:
            parsed.company ||
            parsed.streetLines.split(',')[0] ||
            parsed.streetLines,
          secondary_text: parsed.city
            ? `${parsed.city}, ${parsed.state} ${parsed.zip}`
            : 'Use manually entered address'
        },
        terms: [
          { value: parsed.company || '' },
          { value: parsed.streetLines || '' },
          { value: parsed.city || '' },
          { value: parsed.state || '' },
          { value: parsed.zip || '' }
        ].filter(term => term.value),
        isCustomSuggestion: true,
        parsedAddress: parsed,
        userOriginalInput: address
      };
    };

    // --- HANDLE USER TYPED ADDRESS (when no Google match found) ---
    const handleUserTypedAddress = address => {
      if (!address.trim()) return;

      const parsedAddress = parseUserTypedAddress(address);

      const addressDetails = {
        city: parsedAddress.city || '',
        state: parsedAddress.state || '',
        zip: parsedAddress.zip || '',
        country: parsedAddress.country || 'USA',
        streetLines: parsedAddress.streetLines || address,
        hasStreetLine: Boolean(parsedAddress.streetLines),
        residential: false,
        company: parsedAddress.company || '',
        isUserTyped: true
      };

      setInputValue(address);
      setInputValueApi(address);
      setAddressError('');
      setLastSelectedType('manual');

      console.log('🔍 Manual address processed:', addressDetails);
      onSelectDetails?.(addressDetails);
    };

    // --- CHECK IF ADDRESS LOOKS COMPLETE ---
    const isCompleteAddress = address => {
      if (!address || address.length < 10) return false;

      const hasCompleteAddressFormat = /[A-Za-z\s]+,\s*[A-Z]{2}\s*\d{5}/.test(
        address
      );
      const hasMultipleComponents = address.split(',').length >= 2;

      return hasCompleteAddressFormat || hasMultipleComponents;
    };

    // --- FETCH AUTOCOMPLETE PREDICTIONS WITH CUSTOM SUGGESTIONS ---
    useEffect(() => {
      if (!autocompleteService.current || !inputValue) {
        setOptions([]);
        return;
      }

      const cleanedInput = inputValue.replace(/[^a-zA-Z0-9\s,]/g, '');
      setUserOriginalInput(inputValue);

      if (cleanedInput) {
        autocompleteService.current.getPlacePredictions(
          {
            input: cleanedInput,
            componentRestrictions: { country: ['us', 'ca', 'mx'] }
          },
          predictions => {
            const sanitized = (predictions || []).map(p => ({
              ...p,
              description: inputValue.includes(',')
                ? p.description
                : p.description.replace(/,/g, ''),
              isGoogleSuggestion: true
            }));

            let finalOptions = [...sanitized];

            // Add custom suggestion if user typed a complete-looking address
            if (isCompleteAddress(inputValue)) {
              const customSuggestion = createCustomSuggestion(inputValue);
              // Add custom suggestion at the top
              finalOptions = [customSuggestion, ...sanitized];
            }

            setOptions(finalOptions);
          }
        );
      } else {
        setOptions([]);
      }
    }, [inputValue]);

    // --- CUSTOM RENDER FOR DROPDOWN OPTIONS ---
    const renderOption = (props, option) => {
      const { key, ...otherProps } = props;

      if (option.isCustomSuggestion) {
        const displayText = option.userOriginalInput || option.description;
        // Split the address into logical parts for better display
        const addressParts = displayText
          .split(',')
          .map(part => part.trim())
          .filter(part => part);

        return (
          <li
            {...otherProps}
            key={key}
            style={{
              backgroundColor: '#f0f8ff',
              borderLeft: '4px solid #1890ff',
              padding: '8px 12px',
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <Tooltip
              arrow
              placement='top'
              title='Use your manually entered address'
            >
              <div style={{ width: '100%' }}>
                {/* Main company/street line - ensure it wraps properly */}
                <div
                  style={{
                    color: '#1890ff',
                    fontSize: '14px',
                    lineHeight: '1.3',
                    marginBottom: '2px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {addressParts[0] || displayText}
                </div>

                {/* Address details - show remaining parts */}
                {addressParts.length > 1 && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      lineHeight: '1.3',
                      marginBottom: '4px',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    {addressParts.slice(1).join(', ')}
                  </div>
                )}
              </div>
            </Tooltip>
          </li>
        );
      }

      const mainText =
        option.structured_formatting?.main_text ||
        (option.description ? option.description.split(',')[0] : '');
      const secondaryText =
        option.structured_formatting?.secondary_text ||
        (option.description
          ? option.description.split(',').slice(1).join(',').trim()
          : '');

      return (
        <li
          {...otherProps}
          key={key}
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Tooltip arrow placement='top' title='Google Maps suggestion'>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: '1.3',
                  marginBottom: '2px',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {mainText}
              </div>

              {secondaryText && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    lineHeight: '1.3',
                    marginBottom: '4px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}
                >
                  {secondaryText}
                </div>
              )}
            </div>
          </Tooltip>
        </li>
      );
    };

    // --- CLEAN ADDRESS COMPONENTS ---
    const cleanAddressComponents = components => {
      const extract = (type, useShort = false) =>
        components.find(c => c.types.includes(type))?.[
          useShort ? 'short_name' : 'long_name'
        ] || '';

      const streetNumber = extract('street_number');
      const route = extract('route');

      // Clean street lines - avoid duplication
      let streetLines = '';
      if (streetNumber && route) {
        streetLines = `${streetNumber} ${route}`.trim();
      } else if (route) {
        streetLines = route;
      } else if (streetNumber) {
        streetLines = streetNumber;
      }

      let countryCode = extract('country', true);
      if (countryCode === 'US') countryCode = 'USA';

      return {
        streetNumber,
        route,
        streetLines,
        city: extract('locality') || extract('sublocality') || '',
        state: extract('administrative_area_level_1', true),
        zip: extract('postal_code'),
        country: countryCode
      };
    };

    // --- HANDLE SELECT (Google API selection) ---
    const handleSelect = option => {
      if (
        !option?.place_id ||
        option.place_id === 'custom_user_typed' ||
        option.isCustomSuggestion
      ) {
        // Custom suggestion selected - use user typed address
        handleUserTypedAddress(option?.userOriginalInput || inputValue);
        return;
      }

      // Use PlacesService for quickQuote to get business names
      if (
        inputType === 'quickQuote' &&
        window.google?.maps?.places?.PlacesService
      ) {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );

        service.getDetails(
          {
            placeId: option.place_id,
            fields: ['name', 'address_components', 'formatted_address']
          },
          (place, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place
            ) {
              const components = place.address_components || [];
              const cleaned = cleanAddressComponents(components);

              const addressDetails = {
                city: cleaned.city,
                state: cleaned.state,
                zip: cleaned.zip,
                country: cleaned.country,
                streetLines: cleaned.streetLines,
                hasStreetLine: Boolean(cleaned.streetLines),
                residential: false,
                isUserTyped: false,
                company: '' // Reset company when selecting from Google
              };

              // Only set company if it's a valid business name and not duplicating street address
              if (place.name) {
                const isProbablyCompany = /^[A-Za-z\s&.,'-]+$/.test(
                  place.name.trim()
                );
                const isDuplicateOfStreet =
                  place.name.trim().toLowerCase() ===
                  cleaned.streetLines.toLowerCase();

                if (isProbablyCompany && !isDuplicateOfStreet) {
                  addressDetails.company = place.name.trim();
                }
              }

              const fullAddress =
                place.formatted_address || option.description || '';
              const streetOnly = fullAddress.split(',')[0];
              const finalValue =
                locationFlow === 'LTL' && cleaned.hasStreetLine
                  ? streetOnly
                  : fullAddress;

              const displayValue = addressDetails.company
                ? `${addressDetails.company}, ${fullAddress}`
                : fullAddress;

              setInputValue(displayValue);
              setInputValueApi(finalValue);
              setAddressError('');
              setLastSelectedType('google');

              console.log('🔍 Google address processed:', addressDetails);
              onSelectDetails?.(addressDetails);
            } else {
              // If PlacesService fails, fall back to user typed address
              handleUserTypedAddress(inputValue);
            }
          }
        );
        return;
      }

      // Fallback: geocoder for non-quickQuote or if PlacesService fails
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: option.place_id }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const components = results[0].address_components;
          const cleaned = cleanAddressComponents(components);

          const addressDetails = {
            city: cleaned.city,
            state: cleaned.state,
            zip: cleaned.zip,
            country: cleaned.country,
            streetLines: cleaned.streetLines,
            hasStreetLine: Boolean(cleaned.streetLines),
            residential: false,
            isUserTyped: false,
            company: '' // Reset company for geocoder results
          };

          const fullAddress = results[0].formatted_address;
          const streetOnly = fullAddress.split(',')[0];
          const finalValue =
            locationFlow === 'LTL' && cleaned.hasStreetLine
              ? streetOnly
              : fullAddress;

          setInputValue(fullAddress);
          setInputValueApi(finalValue);
          setAddressError('');
          setLastSelectedType('google');

          console.log('🔍 Geocoder address processed:', addressDetails);
          onSelectDetails?.(addressDetails);
          setHighlightedOption(null);
        } else {
          // If geocoding fails, use user's typed address
          handleUserTypedAddress(inputValue);
        }
      });
    };

    // --- HANDLE BLUR (FIXED) ---
    const handleBlur = () => {
      console.log(
        '🔍 Blur event - inputValue:',
        inputValue,
        'hasUserTyped:',
        hasUserTyped
      );

      // If user typed a complete-looking address but didn't select anything from dropdown
      if (
        inputValue &&
        inputValue !== defaultValue &&
        isCompleteAddress(inputValue) &&
        lastSelectedType !== 'google' // Only auto-process if last selection wasn't from Google
      ) {
        console.log('🔍 Auto-processing manual address on blur');
        handleUserTypedAddress(inputValue);
      }

      setHighlightedOption(null);
      setAddressError('');
    };

    // --- HANDLE KEY PRESS (Enter key) ---
    const handleKeyDown = event => {
      if (event.key === 'Enter') {
        console.log('🔍 Enter pressed - inputValue:', inputValue);

        // If user presses Enter with a complete-looking address
        if (inputValue && isCompleteAddress(inputValue)) {
          console.log('🔍 Auto-processing manual address on Enter');
          handleUserTypedAddress(inputValue);
          event.preventDefault(); // Prevent form submission if inside a form
        }
      }
    };

    return (
      <Tooltip
        title={addressError || validattionErrors?.[errorMsg] || ''}
        arrow
        placement='top-start'
        open={Boolean(addressError || validattionErrors?.[errorMsg])}
      >
        <Autocomplete
          freeSolo
          disabled={disabled}
          clearOnEscape
          disableClearable={false}
          size='small'
          sx={
            locationFlow === 'TL'
              ? {
                  '& .MuiInputBase-root': { fontSize: 13, borderRadius: '8px' },
                  width: '100%',
                  backgroundColor: 'white',
                  '& .MuiAutocomplete-listbox': {
                    padding: 0
                  }
                }
              : {
                  width: '100%',
                  backgroundColor: 'white',
                  '& .MuiAutocomplete-listbox': {
                    padding: 0
                  }
                }
          }
          options={options}
          getOptionLabel={option =>
            typeof option === 'string' ? option : option?.description || ''
          }
          filterOptions={x => x}
          inputValue={inputValue}
          value={
            options.find(opt =>
              typeof opt === 'string'
                ? opt === inputValue
                : opt.description === inputValue
            ) || null
          }
          onHighlightChange={(event, option) => setHighlightedOption(option)}
          onBlur={handleBlur}
          renderOption={renderOption}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              setInputValue(newInputValue);
              setHasUserTyped(true);
              setAddressError('');

              const finalValue =
                locationFlow === 'LTL'
                  ? newInputValue.split(',')[0]
                  : newInputValue;

              onInputChange?.(finalValue);
            }
          }}
          onChange={(event, newValue) => {
            if (newValue) {
              // For custom suggestions, use the exact user input
              if (newValue.isCustomSuggestion) {
                setInputValue(
                  newValue.userOriginalInput || newValue.description
                );
                setInputValueApi(
                  newValue.userOriginalInput || newValue.description
                );
                onInputChange?.(
                  newValue.userOriginalInput || newValue.description
                );
              } else {
                // For Google suggestions, use the normal flow
                const fullAddress = newValue?.description || '';
                const mainText =
                  newValue?.structured_formatting?.main_text || '';
                const descFirst = fullAddress.split(',')[0] || '';
                const termFirst = newValue?.terms?.[0]?.value || '';

                const streetOnly = mainText || descFirst || termFirst || '';
                const finalValue =
                  locationFlow === 'LTL' ? streetOnly : fullAddress;

                const companyCandidate =
                  inputType === 'quickQuote'
                    ? mainText || termFirst || descFirst
                    : null;

                // Only show company if it's not duplicating the street address
                if (
                  inputType === 'quickQuote' &&
                  companyCandidate &&
                  companyCandidate.toLowerCase() !== streetOnly.toLowerCase()
                ) {
                  setInputValue(`${companyCandidate}, ${fullAddress}`);
                } else {
                  setInputValue(fullAddress);
                }

                setInputValueApi(finalValue);
                onInputChange?.(finalValue);
              }

              handleSelect(newValue);
              setAddressError('');
            } else {
              setInputValue('');
              setInputValueApi('');
              onInputChange?.('');
              onSelectDetails?.(null);
              setAddressError('');
              setLastSelectedType(null);
            }
          }}
          renderInput={params => (
            <TextField
              inputRef={inputRef}
              placeholder={
                locationFlow === 'TLMultiDropCase'
                  ? 'Search Address with Google API'
                  : locationFlow === 'TL'
                    ? 'e.g. 123 Main St, Springfield, IL 62704'
                    : ''
              }
              {...params}
              error={
                Boolean(validattionErrors?.[errorMsg]) || Boolean(addressError)
              }
              size='small'
              fullWidth
              variant={inputVariant}
              label={locationFlow === 'TLMultiDropCase' ? '' : label}
              onKeyDown={handleKeyDown} // Added keydown handler
              inputProps={{
                ...params.inputProps,
                style:
                  inputVariant === 'standard'
                    ? { fontSize: fontSize }
                    : locationFlow === 'TLMultiDropCase'
                      ? { fontSize: fontSize, height: 14 }
                      : { fontSize: fontSize }
              }}
            />
          )}
        />
      </Tooltip>
    );
  }
);

export default AddressAutocomplete;
